import React, { useState } from 'react';
import { ChecklistRunner } from '../components/ChecklistRunner';
import { availableChecklists, ChecklistId } from '../lib/checklists';

export const ChecklistDemo: React.FC = () => {
  const [selectedChecklist, setSelectedChecklist] = useState<ChecklistId>('auth-setup');
  const [showChecklist, setShowChecklist] = useState(false);

  const handleChecklistSelect = (checklistId: ChecklistId) => {
    setSelectedChecklist(checklistId);
    setShowChecklist(true);
  };

  const handleBackToSelection = () => {
    setShowChecklist(false);
  };

  if (showChecklist) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="mb-6">
            <button
              onClick={handleBackToSelection}
              className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-4"
            >
              ← Back to Checklist Selection
            </button>
          </div>

          <ChecklistRunner
            config={availableChecklists[selectedChecklist]}
            className="space-y-6"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            🔧 GoalYet Setup Checklists
          </h1>
          <p className="text-lg text-gray-600">
            Animated, step-by-step setup processes with real-time progress tracking
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Object.entries(availableChecklists).map(([id, config]) => (
            <div
              key={id}
              className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow cursor-pointer border-2 border-transparent hover:border-blue-200"
              onClick={() => handleChecklistSelect(id as ChecklistId)}
            >
              <div className="text-center mb-4">
                <div className="text-4xl mb-3">
                  {config.title.split(' ')[0]} {/* Extract emoji */}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {config.title.substring(2)} {/* Remove emoji */}
                </h3>
                <p className="text-gray-600 text-sm">
                  {config.description}
                </p>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">Steps:</span>
                  <span className="text-sm font-bold text-blue-600">
                    {config.steps.length}
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">Est. Time:</span>
                  <span className="text-sm font-bold text-green-600">
                    {Math.floor(config.estimatedTotalTime / 60)}m {config.estimatedTotalTime % 60}s
                  </span>
                </div>

                <div className="pt-3 border-t border-gray-200">
                  <div className="text-xs text-gray-500 mb-2">Categories:</div>
                  <div className="flex flex-wrap gap-1">
                    {Array.from(new Set(config.steps.map(step => step.category))).map(category => (
                      <span
                        key={category}
                        className={`px-2 py-1 text-xs rounded-full ${getCategoryStyles(category)}`}
                      >
                        {category}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200">
                <button className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2">
                  ▶️ Start Checklist
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            🎯 How Checklists Work
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Features</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center gap-2">
                  <span className="text-green-500">✅</span>
                  Real-time animated progress tracking
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-blue-500">⚡</span>
                  Automatic step execution with timing
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-purple-500">🔄</span>
                  Retry failed steps individually
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-orange-500">📊</span>
                  Category-based step organization
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-pink-500">🎨</span>
                  Beautiful animated UI with Framer Motion
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-indigo-500">🔗</span>
                  Dependency management between steps
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Step Categories</h3>
              <div className="space-y-2">
                {[
                  { category: 'user', description: 'User actions & requirements' },
                  { category: 'frontend', description: 'React app & UI components' },
                  { category: 'backend', description: 'API endpoints & workers' },
                  { category: 'database', description: 'D1 database & schema' },
                  { category: 'storage', description: 'R2 file storage & uploads' },
                  { category: 'network', description: 'CORS, auth & connections' },
                  { category: 'response', description: 'Final validation & results' }
                ].map(({ category, description }) => (
                  <div key={category} className="flex items-center gap-3">
                    <span className={`px-2 py-1 text-xs rounded-full font-medium ${getCategoryStyles(category)}`}>
                      {category}
                    </span>
                    <span className="text-sm text-gray-600">{description}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start gap-3">
              <span className="text-blue-600 text-xl">💡</span>
              <div>
                <h4 className="font-semibold text-blue-800 mb-1">Pro Tip</h4>
                <p className="text-blue-700 text-sm">
                  Each checklist runs real API calls and verifies your actual development environment.
                  Watch the progress in real-time and see exactly which step fails if there are issues!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function to get category styles
const getCategoryStyles = (category: string) => {
  const styles = {
    user: 'bg-purple-100 text-purple-700 border-purple-300',
    frontend: 'bg-blue-100 text-blue-700 border-blue-300',
    network: 'bg-yellow-100 text-yellow-700 border-yellow-300',
    backend: 'bg-orange-100 text-orange-700 border-orange-300',
    database: 'bg-green-100 text-green-700 border-green-300',
    storage: 'bg-indigo-100 text-indigo-700 border-indigo-300',
    response: 'bg-pink-100 text-pink-700 border-pink-300'
  };
  return styles[category as keyof typeof styles] || 'bg-gray-100 text-gray-700 border-gray-300';
};